import React, { useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import logger from '@helpers/utils/logger';
import { datadogRum } from '@datadog/browser-rum-slim';
import useWindowSize from '@hooks/use-window-size';
import AlertsService from '@services/alerts-service';
import { AlertOrderType, AlertWithProducts } from '@interfaces/models/alert';
import { CatalogProduct } from '@interfaces/models/catalogProduct';
import ProductsList, { ProductListLoadingState } from '@components/generic-product-feed/products-list/products-list';
import Pagination from '@components/catalog/pagination/pagination';
import AlertPausedFlag from '@components/alerts/alert-paused-flag/alert-paused-flag';

import styles from './alerts-content.module.scss';

const maxNotificationsCounter = 999;
const limitValues = [8, 16];

interface EmptyAlertMessageProps {
  alert: AlertWithProducts;
  sortBy: AlertOrderType | undefined;
}

const EmptyAlertMessage: React.FC<EmptyAlertMessageProps> = ({ alert, sortBy }) => {
  const { t } = useTranslation();
  useEffect(() => {
    logger.info(
      {
        id: alert.id,
        title: alert.title,
        filters: alert.subtitle,
        sortBy,
      },
      'alerts::AlertsCatalogPage::alert-is-empty',
    );
    datadogRum.addError('alerts::AlertsCatalogPage::alert-is-empty', {
      id: alert.id,
      title: alert.title,
      filters: alert.subtitle,
      sortBy,
    });
  }, [alert.id, alert.title, alert.subtitle, sortBy]);
  return <p className={styles.alertsContent__flexContainer__noitem}>{t('ALERTS.LANDING.PRODUCT.EMPTY.TEXT')}</p>;
};

interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalCount?: number;
}

export interface AlertsContentProps {
  alertsList: AlertWithProducts[];
  isLoading: boolean;
  error: string | null;
  pagination: PaginationState;
  queryParams: { pageId: number; limit: number } | undefined;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onRedirectViewAll: (alert: AlertWithProducts) => void;
  country: string;
  currency: string;
  language: string;
  sortBy: AlertOrderType | undefined;
}

const AlertsContent: React.FC<AlertsContentProps> = ({
  alertsList,
  isLoading,
  error,
  pagination,
  queryParams,
  sortBy,
  onPageChange,
  onLimitChange,
  onRedirectViewAll,
}) => {
  const { t } = useTranslation();
  const { t: tA11y } = useTranslation('a11y');
  const { isWiderThanMd } = useWindowSize();
  const hasNewTag = (alert: AlertWithProducts, i: number) => {
    return !!alert.counter && i + 1 <= alert.counter;
  };

  return (
    <div>
      <div className={clsx(styles.alertsContent, 'innerContainer')}>
        {error && <div className={styles.alertsContent__error}>{t('ALERTS.LANDING.ERROR')}</div>}
        {isLoading && !error && <ProductListLoadingState className={styles.alertsContent__loader} />}
        {alertsList && alertsList.length && !isLoading
          ? alertsList.map((alert: AlertWithProducts, alertListIndex: number) => (
              <div
                className={clsx(
                  styles.alertsContent__columnProductList,
                  alertListIndex === alertsList.length - 1 && styles['alertsContent__columnProductList--last'],
                )}
                key={`${alert.id}_${alertListIndex.toString()}`}
              >
                <div className={styles.alertsContent__columnProductList__header}>
                  <div className={styles.alertsContent__columnProductList__header__left}>
                    <div
                      className={styles.alertsContent__columnProductList__header__left__title}
                      data-cy={`alerts__productList__title__${alert.id}`}
                    >
                      {alert.title}
                    </div>
                    <div className={clsx(styles.alertsContent__columnProductList__header__left__filter, 'vc-text-m')}>
                      {alert.subtitle && (
                        <>
                          {t('ALERTS.LANDING.FILTERS_TEXT')} {alert.subtitle}
                        </>
                      )}
                    </div>

                    <div className={clsx(styles.alertsContent__columnProductList__header__left__counter, 'vc-text-m')}>
                      {alert.isPaused ? (
                        <AlertPausedFlag showIcon />
                      ) : (
                        <span
                          className={clsx(
                            alert.counter &&
                              !!alert.items?.length &&
                              styles.alertsContent__columnProductList__header__left__counter__new,
                          )}
                        >
                          {alert.items?.length > 0 && alert.counter > 1
                            ? [
                                '+',
                                Math.min(alert.counter, maxNotificationsCounter),
                                ' ',
                                t('ALERTS.LANDING.ITEM_COUNTER_TEXT.PLURAL'),
                              ].join('')
                            : `${alert.counter === 1 && alert.items?.length ? `+${alert.counter}` : '0'} ${t(
                                'ALERTS.LANDING.ITEM_COUNTER_TEXT.SINGULAR',
                              )}`}
                        </span>
                      )}
                    </div>
                  </div>
                  {!!alert.items?.length && (
                    <button
                      type="button"
                      className={clsx(
                        'vc-btn vc-btn--rounded-secondary',
                        isWiderThanMd ? 'vc-btn--large' : 'vc-btn--small',
                      )}
                      onClick={() => onRedirectViewAll(alert)}
                      data-cy={`alerts__productList__viewAllBtn__${alert.id}`}
                    >
                      {t('ALERTS.LANDING.VIEWALL_TEXT')}
                    </button>
                  )}
                </div>

                {alert.items?.length ? (
                  <ProductsList
                    products={alert.items as unknown as CatalogProduct[]}
                    pageType={'alerts'}
                    className={styles.alertsContent__flexContainer}
                    productCardProps={(alertProductIndex) => ({
                      isNewProduct: hasNewTag(alert, alertProductIndex),
                      disableLazyLoad: alertListIndex === 0,
                      pageType: 'alerts',
                      fetchpriority: alertListIndex === 0 ? 'high' : 'low',
                      trackingAlerts: { ...alert, sortBy: sortBy },
                      onClick: () => {
                        AlertsService.resetCounter(alert.id);
                      },
                    })}
                  />
                ) : (
                  <EmptyAlertMessage
                    alert={alert}
                    sortBy={sortBy}
                  />
                )}
              </div>
            ))
          : null}

        {alertsList && alertsList.length > 0 && queryParams && (
          <div className={styles.alertsContent__pagination}>
            {isWiderThanMd && (
              <div aria-label={tA11y('A11Y.RESULTS_SELECTED', { itemsPerPage: queryParams.limit })}>
                {t('CATALOG.FILTERS.HITS_PER_PAGE_LABEL')}
                {limitValues.map((v) => (
                  <button
                    type="button"
                    key={v}
                    className={clsx(styles.alertsContent__pagination__limitButton, {
                      [styles['alertsContent__pagination__limitButton--active']]: queryParams.limit === v,
                    })}
                    onClick={() => onLimitChange(v)}
                    aria-label={tA11y('A11Y.RESULTS_NUMBER', { itemsPerPage: v })}
                    aria-current={queryParams.limit === v ? 'true' : undefined}
                  >
                    {v}
                  </button>
                ))}
              </div>
            )}
            <Pagination
              currentPage={pagination.currentPage}
              pageCount={pagination.totalPages}
              showText={false}
              hasArrows={true}
              buttonsToShow={5}
              customActiveStyle={false}
              onChange={(page) => onPageChange(page)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertsContent;
